import { useMemo, useState } from "react";
import { Loader2, Sparkles, ArrowLeft, CheckCircle2, AlertTriangle } from "lucide-react";
import { motion } from "framer-motion";
import { api, formatApiError, fieldErrorsFromApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { formatPhp } from "@/lib/format";

const PROPERTY_CATEGORIES = [
  "House & Lot",
  "Lot",
  "Condominium",
  "Townhouse",
  "Duplex",
  "Triplex",
  "Apartment",
  "Warehouse",
  "Commercial",
  "Industrial",
  "Agricultural",
];

const TRANSACTION_TYPES_LISTING = ["For Sale", "For Lease"];
const TRANSACTION_TYPES_REQUIREMENT = ["Wanted to Buy", "Wanted to Lease"];

function computePricePerSqm(price, floor, lot) {
  const p = Number(price) || 0;
  if (p <= 0) return null;
  const f = Number(floor) || 0;
  if (f > 0) return Math.round((p / f) * 100) / 100;
  const l = Number(lot) || 0;
  if (l > 0) return Math.round((p / l) * 100) / 100;
  return null;
}

/**
 * `mode` = "listing" | "requirement"
 *
 * Runs the 3-stage flow (paste → parse → editable preview → save) and
 * calls `onConflict` when the API returns 409 EXCLUSIVE_ATS_CONFLICT so
 * the parent can raise the calm conflict modal.
 */
export function AIParseDialog({
  open,
  onOpenChange,
  mode = "listing",
  onSaved,
  onConflict,
  onUpgradeRequired,
}) {
  const [stage, setStage] = useState("paste");
  const [rawText, setRawText] = useState("");
  const [form, setForm] = useState(null);
  const [error, setError] = useState("");
  // Field-level errors (e.g. { building_or_project: "Building / project
  // is required." }). Rendered inline under the offending input AND
  // used to gate Save. NEVER silently disables — clicking Save with
  // problems present now surfaces them.
  const [fieldErrors, setFieldErrors] = useState({});

  const isListing = mode === "listing";
  const title = isListing ? "Add listing" : "Add requirement";
  const placeholder = isListing
    ? "e.g. Warehouse for lease, Dasmariñas Cavite, 1,200sqm, 40-ft truck accessible, 3-phase electricity, flood-free, ₱180/sqm monthly."
    : "e.g. OFW client wants to BUY a 2BR condo in BGC under ₱15M, near international schools, RFO preferred.";

  const reset = () => {
    setStage("paste");
    setRawText("");
    setForm(null);
    setError("");
    setFieldErrors({});
  };

  const handleClose = (nextOpen) => {
    if (!nextOpen) reset();
    onOpenChange(nextOpen);
  };

  const handleParse = async () => {
    setError("");
    setStage("loading");
    try {
      const { data } = await api.post(
        isListing ? "/ai/parse-listing" : "/ai/parse-requirement",
        { raw_text: rawText }
      );
      setForm({
        ...data,
        preferred_features: data.preferred_features || [],
        semantic_features: data.semantic_features || "",
      });
      setStage("preview");
    } catch (e) {
      setStage("paste");
      setError(
        formatApiError(e, "AI parsing failed. Please try again with more detail.")
      );
    }
  };

  const setField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    // Clear the inline error for this field as the user types.
    setFieldErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  /**
   * Client-side pre-save validation — surfaces required fields
   * inline BEFORE we hit the API. The old implementation silently
   * disabled the Save button, which read as "the app is broken" to
   * users typing natural raw text (e.g. no explicit building name).
   * Returns null when valid, or a { field: msg } map when invalid.
   */
  const collectMissingRequired = () => {
    if (!form) return null;
    const errs = {};
    if (isListing) {
      if (!(form.title || "").trim()) errs.title = "Title is required.";
      if (!(form.location || "").trim()) errs.location = "Location is required.";
      if (!(form.building_or_project || "").trim())
        errs.building_or_project =
          "Building / project is required — the raw text didn't mention one. Type it manually to save.";
      const price = Number(form.price_gross ?? form.price ?? 0);
      if (!(price > 0)) errs.price_gross = "Enter the listing price.";
    } else {
      if (!(form.target_location || "").trim())
        errs.target_location = "Target location is required.";
      const budget = Number(form.budget_max ?? form.max_budget ?? 0);
      if (!(budget > 0)) errs.budget_max = "Enter the buyer's max budget.";
    }
    return Object.keys(errs).length ? errs : null;
  };

  const handleSave = async () => {
    setError("");
    // Pre-flight: surface missing required fields inline BEFORE the
    // API call. This is the P0 fix — the old code silently disabled
    // the Save button, leaving the user staring at a grey CTA with
    // no idea why.
    const missing = collectMissingRequired();
    if (missing) {
      setFieldErrors(missing);
      setError(
        "Please fill in the highlighted fields — the AI couldn't extract them from the raw text.",
      );
      // Focus the first offending input so screen readers + keyboard
      // users get a jump.
      const first = Object.keys(missing)[0];
      if (first) {
        setTimeout(() => {
          const el = document.querySelector(`[data-testid="preview-${first.replace(/_/g, "-")}"]`);
          if (el && typeof el.focus === "function") el.focus();
        }, 50);
      }
      return;
    }
    setFieldErrors({});
    setStage("saving");
    try {
      if (isListing) {
        const payload = {
          raw_text: rawText,
          title: form.title || "Untitled listing",
          transaction_type: form.transaction_type || "For Sale",
          property_category: form.property_category || "Condominium",
          location: form.location || "",
          building_or_project: form.building_or_project || "",
          unit_identifier: form.unit_identifier || "",
          bedrooms: Number(form.bedrooms) || 0,
          lot_area_sqm: form.lot_area_sqm ? Number(form.lot_area_sqm) : null,
          floor_area_sqm: form.floor_area_sqm ? Number(form.floor_area_sqm) : null,
          parking_slots:
            form.parking_slots === "" || form.parking_slots == null
              ? null
              : Number(form.parking_slots),
          price_gross: Number(form.price_gross ?? form.price) || 0,
          semantic_features: form.semantic_features || "",
          exclusive_ats: !!form.exclusive_ats,
        };
        const { data } = await api.post("/listings", payload);
        toast.success("Listing added.");
        onSaved?.(data);
        handleClose(false);
      } else {
        const features = Array.isArray(form.preferred_features)
          ? form.preferred_features
          : String(form.preferred_features || "")
              .split(",")
              .map((s) => s.trim())
              .filter(Boolean);
        const payload = {
          raw_text: rawText,
          target_location: form.target_location || "",
          transaction_type: form.transaction_type || "Wanted to Buy",
          property_category: form.property_category || "Condominium",
          bedrooms: Number(form.bedrooms) || 0,
          lot_area_sqm: form.lot_area_sqm ? Number(form.lot_area_sqm) : null,
          floor_area_sqm: form.floor_area_sqm ? Number(form.floor_area_sqm) : null,
          parking_slots:
            form.parking_slots === "" || form.parking_slots == null
              ? null
              : Number(form.parking_slots),
          budget_max: Number(form.budget_max ?? form.max_budget) || 0,
          preferred_features: features,
          semantic_features: form.semantic_features || "",
          notes: form.notes || "",
        };
        const { data } = await api.post("/requirements", payload);
        toast.success("Requirement added.");
        onSaved?.(data);
        handleClose(false);
      }
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 402 && detail === "UPGRADE_REQUIRED") {
        handleClose(false);
        setTimeout(() => {
          onUpgradeRequired?.();
        }, 200);
        return;
      }
      if (
        status === 409 &&
        detail &&
        typeof detail === "object" &&
        detail.detail === "EXCLUSIVE_ATS_CONFLICT"
      ) {
        handleClose(false);
        setTimeout(() => {
          onConflict?.({
            kind: "EXCLUSIVE_ATS_CONFLICT",
            disputeId: detail.dispute_id,
            conflictingListingId: detail.conflicting_listing_id,
          });
        }, 200);
        return;
      }
      if (status === 409 && detail === "DUPLICATE_LISTING") {
        handleClose(false);
        setTimeout(() => {
          onConflict?.({ kind: "DUPLICATE_LISTING" });
        }, 200);
        return;
      }
      // 422 → paint inline field errors (never leave the user in the
      // dark). 4xx/5xx → banner at top of preview.
      setStage("preview");
      const inline = fieldErrorsFromApiError(e);
      if (status === 422 && inline) {
        setFieldErrors(inline);
        setError("Some fields need attention before we can save.");
        // Toast a compact summary so the fix is discoverable if the
        // form is scrolled.
        toast.error(formatApiError(e, "Save failed."));
        return;
      }
      if (status >= 500) {
        const msg = "Something went wrong on our side. Please try again in a moment.";
        setError(msg);
        toast.error(msg);
        return;
      }
      const msg = formatApiError(e, "Save failed.");
      setError(msg);
      toast.error(msg);
    }
  };

  const derivedPricePerSqm = useMemo(() => {
    if (!form || !isListing) return null;
    return computePricePerSqm(
      form.price_gross ?? form.price,
      form.floor_area_sqm,
      form.lot_area_sqm
    );
  }, [form, isListing]);

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        className="max-w-3xl max-h-[90vh] overflow-y-auto rounded-2xl border-gray-200 bg-white p-8 sm:p-10"
        data-testid={`ai-parse-dialog-${mode}`}
      >
        <div className="flex items-start gap-3">
          <span className="mt-1 flex h-9 w-9 items-center justify-center rounded-full bg-[#1B2B4B]/10 text-[#1B2B4B]">
            <Sparkles className="h-4 w-4" />
          </span>
          <div>
            <DialogTitle
              className="text-2xl font-bold tracking-tight text-gray-900"
              data-testid="ai-parse-dialog-title"
            >
              {title}
            </DialogTitle>
            <DialogDescription className="text-sm text-gray-500">
              {isListing
                ? "Paste the broker's raw write-up. The AI will structure it — you can correct anything before saving."
                : "Paste the buyer's requirements as you'd describe them to a peer. The AI will extract the key fields."}
            </DialogDescription>
          </div>
        </div>

        {stage === "paste" && (
          <div className="mt-8 space-y-5">
            <div className="space-y-2">
              <Label htmlFor="ai-raw-text" className="text-gray-700">
                Raw text
              </Label>
              <Textarea
                id="ai-raw-text"
                value={rawText}
                onChange={(e) => setRawText(e.target.value)}
                placeholder={placeholder}
                className="mb-focus-ring min-h-[240px] rounded-xl border-gray-200 bg-white text-base"
                data-testid="ai-raw-text-input"
              />
              <p className="text-xs text-gray-500">
                English, Filipino or Taglish — the AI understands both. Include price, exact
                address, and any PH-specific constraints (flood-free, 40-ft truck accessible,
                3-phase, non-tumbok, RFO, etc.) — they drive matching.
              </p>
            </div>
            {error && (
              <div
                className="rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm font-medium text-red-700"
                data-testid="ai-parse-error"
              >
                {error}
              </div>
            )}
            <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
              <Button
                type="button"
                variant="outline"
                onClick={() => handleClose(false)}
                className="mb-focus-ring h-11 rounded-xl border-gray-200 bg-white px-6 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                data-testid="ai-cancel-button"
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={handleParse}
                disabled={rawText.trim().length < 8}
                className="mb-focus-ring h-11 rounded-xl bg-[#1B2B4B] px-6 text-sm font-semibold text-white hover:bg-[#14213A] disabled:opacity-60"
                data-testid="ai-parse-button"
              >
                <Sparkles className="mr-2 h-4 w-4" />
                Parse with AI
              </Button>
            </div>
          </div>
        )}

        {stage === "loading" && (
          <div className="mt-8 space-y-5" data-testid="ai-parse-loading">
            <div className="space-y-2">
              <Label className="text-gray-700">Raw text</Label>
              <div className="relative overflow-hidden rounded-xl border border-gray-200 bg-white">
                <Textarea
                  value={rawText}
                  readOnly
                  className="min-h-[240px] rounded-xl border-0 bg-transparent text-base focus-visible:ring-0 focus-visible:ring-offset-0"
                />
                <div
                  className="mb-parse-scan"
                  style={{ ["--mb-scan-h"]: "280px" }}
                  aria-hidden="true"
                  data-testid="ai-parse-scan-line"
                />
              </div>
            </div>
            <div className="flex items-center justify-center gap-2 text-sm font-medium text-[#35622F]">
              <Sparkles className="h-4 w-4 animate-pulse" />
              {isListing
                ? "AI is reading your listing…"
                : "AI is reading the requirement…"}
            </div>
            <p className="text-center text-xs text-gray-500">
              Usually takes 3–8 seconds.
            </p>
          </div>
        )}

        {(stage === "preview" || stage === "saving") && form && (
          <motion.div
            className="mt-8 space-y-6"
            data-testid="ai-preview-form"
            initial="hidden"
            animate="visible"
            variants={{
              hidden: {},
              visible: {
                transition: {
                  staggerChildren: 0.08,
                  delayChildren: 0.05,
                },
              },
            }}
          >
            {isListing ? (
              <>
                <FormField
                  label="Title"
                  value={form.title}
                  onChange={(v) => setField("title", v)}
                  testId="preview-title"
                  error={fieldErrors.title}
                />
                <motion.div className="grid grid-cols-1 gap-5 sm:grid-cols-2" variants={fieldVariants}>
                  <EnumSelect
                    label="Transaction"
                    options={TRANSACTION_TYPES_LISTING}
                    value={form.transaction_type || "For Sale"}
                    onChange={(v) => setField("transaction_type", v)}
                    testId="preview-transaction-type"
                    error={fieldErrors.transaction_type}
                  />
                  <EnumSelect
                    label="Property category"
                    options={PROPERTY_CATEGORIES}
                    value={form.property_category || "Condominium"}
                    onChange={(v) => setField("property_category", v)}
                    testId="preview-property-category"
                    error={fieldErrors.property_category}
                  />
                  <FormField
                    label="Building / Project"
                    value={form.building_or_project}
                    onChange={(v) => setField("building_or_project", v)}
                    testId="preview-building"
                    error={fieldErrors.building_or_project}
                  />
                  <FormField
                    label="Unit identifier"
                    value={form.unit_identifier}
                    onChange={(v) => setField("unit_identifier", v)}
                    testId="preview-unit"
                    placeholder="Tower / Unit / Lot"
                    error={fieldErrors.unit_identifier}
                  />
                  <FormField
                    label="Location"
                    value={form.location}
                    onChange={(v) => setField("location", v)}
                    testId="preview-location"
                    error={fieldErrors.location}
                  />
                  <FormField
                    label="Bedrooms"
                    type="number"
                    value={form.bedrooms}
                    onChange={(v) => setField("bedrooms", v)}
                    testId="preview-bedrooms"
                    error={fieldErrors.bedrooms}
                  />
                  <FormField
                    label="Lot area (sqm)"
                    type="number"
                    value={form.lot_area_sqm ?? ""}
                    onChange={(v) => setField("lot_area_sqm", v)}
                    testId="preview-lot-area"
                    placeholder="e.g. 550"
                    error={fieldErrors.lot_area_sqm}
                  />
                  <FormField
                    label="Floor area (sqm)"
                    type="number"
                    value={form.floor_area_sqm ?? ""}
                    onChange={(v) => setField("floor_area_sqm", v)}
                    testId="preview-floor-area"
                    placeholder="e.g. 58"
                    error={fieldErrors.floor_area_sqm}
                  />
                  <FormField
                    label="Parking slots"
                    type="number"
                    value={form.parking_slots ?? ""}
                    onChange={(v) => setField("parking_slots", v)}
                    testId="preview-parking-slots"
                    placeholder="e.g. 1"
                    error={fieldErrors.parking_slots}
                  />
                  <FormField
                    label={
                      (form.transaction_type || "For Sale") === "For Lease"
                        ? "Monthly rate (PHP)"
                        : "Price (PHP)"
                    }
                    type="number"
                    value={form.price_gross ?? form.price ?? 0}
                    onChange={(v) => setField("price_gross", v)}
                    testId="preview-price"
                    error={fieldErrors.price_gross || fieldErrors.price}
                  />
                </motion.div>
                {derivedPricePerSqm != null && (
                  <div
                    className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-700"
                    data-testid="preview-price-per-sqm"
                  >
                    Auto-computed{" "}
                    <span className="font-semibold text-gray-900">
                      {formatPhp(derivedPricePerSqm)} / sqm
                    </span>
                  </div>
                )}
                <div className="space-y-2">
                  <Label className="text-gray-700">Key Property Highlights</Label>
                  <Textarea
                    value={form.semantic_features || ""}
                    onChange={(e) => setField("semantic_features", e.target.value)}
                    className="mb-focus-ring min-h-[80px] rounded-xl border-gray-200 bg-white text-base"
                    placeholder="comma-joined — e.g. flood-free, 40-ft truck accessible, 3-phase electricity, non-tumbok, RFO"
                    data-testid="preview-semantic-features"
                  />
                  <p className="text-xs text-gray-500">
                    Short, comma-separated highlights (PH-specific constraints,
                    build features, location perks). These drive AI matching —
                    only include what&apos;s actually in the source text.
                  </p>
                </div>
                <div className="flex items-center justify-between rounded-xl border border-gray-200 bg-gray-50 px-4 py-3">
                  <div>
                    <div className="text-sm font-semibold text-gray-900">
                      Exclusive Authority to Sell
                    </div>
                    <div className="text-xs text-gray-500">
                      Toggle on only if you hold an exclusive ATS for this unit.
                    </div>
                  </div>
                  <Switch
                    checked={!!form.exclusive_ats}
                    onCheckedChange={(v) => setField("exclusive_ats", v)}
                    data-testid="preview-exclusive-ats-switch"
                    className="data-[state=checked]:bg-[#35622F]"
                  />
                </div>
              </>
            ) : (
              <>
                <motion.div className="grid grid-cols-1 gap-5 sm:grid-cols-2" variants={fieldVariants}>
                  <EnumSelect
                    label="Buyer intent"
                    options={TRANSACTION_TYPES_REQUIREMENT}
                    value={form.transaction_type || "Wanted to Buy"}
                    onChange={(v) => setField("transaction_type", v)}
                    testId="preview-transaction-type"
                    error={fieldErrors.transaction_type}
                  />
                  <EnumSelect
                    label="Property category"
                    options={PROPERTY_CATEGORIES}
                    value={form.property_category || "Condominium"}
                    onChange={(v) => setField("property_category", v)}
                    testId="preview-property-category"
                    error={fieldErrors.property_category}
                  />
                  <FormField
                    label="Target location"
                    value={form.target_location}
                    onChange={(v) => setField("target_location", v)}
                    testId="preview-target-location"
                    error={fieldErrors.target_location}
                  />
                  <FormField
                    label="Bedrooms"
                    type="number"
                    value={form.bedrooms ?? 0}
                    onChange={(v) => setField("bedrooms", v)}
                    testId="preview-bedrooms"
                    error={fieldErrors.bedrooms}
                  />
                  <FormField
                    label="Min lot area (sqm)"
                    type="number"
                    value={form.lot_area_sqm ?? ""}
                    onChange={(v) => setField("lot_area_sqm", v)}
                    testId="preview-lot-area"
                    placeholder="optional"
                    error={fieldErrors.lot_area_sqm}
                  />
                  <FormField
                    label="Min floor area (sqm)"
                    type="number"
                    value={form.floor_area_sqm ?? ""}
                    onChange={(v) => setField("floor_area_sqm", v)}
                    testId="preview-floor-area"
                    placeholder="optional"
                    error={fieldErrors.floor_area_sqm}
                  />
                  <FormField
                    label="Parking slots"
                    type="number"
                    value={form.parking_slots ?? ""}
                    onChange={(v) => setField("parking_slots", v)}
                    testId="preview-parking-slots"
                    placeholder="optional"
                    error={fieldErrors.parking_slots}
                  />
                  <FormField
                    label={
                      (form.transaction_type || "Wanted to Buy") === "Wanted to Lease"
                        ? "Max monthly budget (PHP)"
                        : "Max budget (PHP)"
                    }
                    type="number"
                    value={form.budget_max ?? form.max_budget ?? 0}
                    onChange={(v) => setField("budget_max", v)}
                    testId="preview-budget-max"
                    error={fieldErrors.budget_max}
                  />
                  <FormField
                    label="Preferred features (comma separated)"
                    value={
                      Array.isArray(form.preferred_features)
                        ? form.preferred_features.join(", ")
                        : String(form.preferred_features || "")
                    }
                    onChange={(v) =>
                      setField(
                        "preferred_features",
                        v.split(",").map((s) => s.trim()).filter(Boolean)
                      )
                    }
                    testId="preview-features"
                    error={fieldErrors.preferred_features}
                  />
                </motion.div>
                <div className="space-y-2">
                  <Label className="text-gray-700">
                    Key Property Highlights (what the buyer wants)
                  </Label>
                  <Textarea
                    value={form.semantic_features || ""}
                    onChange={(e) => setField("semantic_features", e.target.value)}
                    className="mb-focus-ring min-h-[80px] rounded-xl border-gray-200 bg-white text-base"
                    placeholder="e.g. flood-free, 3-phase, gated community, RFO, near international schools"
                    data-testid="preview-semantic-features"
                  />
                  <p className="text-xs text-gray-500">
                    Short, comma-separated highlights the buyer specifically
                    asked for. These drive AI matching.
                  </p>
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-700">Notes</Label>
                  <Textarea
                    value={form.notes || ""}
                    onChange={(e) => setField("notes", e.target.value)}
                    className="mb-focus-ring min-h-[100px] rounded-xl border-gray-200 bg-white text-base"
                    data-testid="preview-notes"
                  />
                </div>
              </>
            )}

            {error && (
              <div
                className="flex items-start gap-2 rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm font-medium text-red-700"
                data-testid="ai-parse-error"
                role="alert"
              >
                <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-between">
              <Button
                type="button"
                variant="outline"
                onClick={() => setStage("paste")}
                className="mb-focus-ring h-11 rounded-xl border-gray-200 bg-white px-6 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                data-testid="preview-back-button"
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to edit
              </Button>
              <Button
                type="button"
                onClick={handleSave}
                disabled={stage === "saving"}
                className="mb-focus-ring h-11 rounded-xl bg-[#1B2B4B] px-6 text-sm font-semibold text-white hover:bg-[#14213A] disabled:opacity-60"
                data-testid="preview-save-button"
              >
                {stage === "saving" ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving…
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="mr-2 h-4 w-4" />
                    Confirm &amp; save
                  </>
                )}
              </Button>
            </div>
          </motion.div>
        )}
      </DialogContent>
    </Dialog>
  );
}

const fieldVariants = {
  hidden: { opacity: 0, y: -8 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.32, ease: [0.22, 1, 0.36, 1] },
  },
};

function FormField({ label, value, onChange, testId, type = "text", placeholder, error }) {
  return (
    <motion.div className="space-y-2" variants={fieldVariants}>
      <Label className="text-gray-700">{label}</Label>
      <Input
        type={type}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        aria-invalid={!!error}
        className={`mb-focus-ring h-11 rounded-xl bg-white text-base ${
          error ? "border-red-300 focus-visible:ring-red-200" : "border-gray-200"
        }`}
        data-testid={testId}
      />
      {error && (
        <p
          className="text-xs font-medium text-red-600"
          data-testid={`${testId}-error`}
        >
          {error}
        </p>
      )}
    </motion.div>
  );
}

function EnumSelect({ label, options, value, onChange, testId, error }) {
  return (
    <motion.div className="space-y-2" variants={fieldVariants}>
      <Label className="text-gray-700">{label}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger
          aria-invalid={!!error}
          className={`mb-focus-ring h-11 rounded-xl bg-white text-base ${
            error ? "border-red-300 focus-visible:ring-red-200" : "border-gray-200"
          }`}
          data-testid={testId}
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {options.map((t) => (
            <SelectItem key={t} value={t}>
              {t}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {error && (
        <p
          className="text-xs font-medium text-red-600"
          data-testid={`${testId}-error`}
        >
          {error}
        </p>
      )}
    </motion.div>
  );
}
